import { carnotHttpClient, dataHttpClient, measureHttpClient } from '@/services/httpClient';
import { handleError, handleResponse } from '@/store/constant';

const getInitialState = () => ({
  projectsArray: [],
  projectSelected: {},
  projectMetadata: {},
  projectsFlightDatesList: [],
  isAddSidebar: true,
  currentPath: '',
  weather: {},
  weatherByProjects: {},
  metadataByProjects: {},
  isFlightDateFetched: false,
});

const state = getInitialState();

const actions = {
  async getAllProjects({ commit, state, dispatch }) {
    if (state.projectsArray.length) return;

    dispatch('toggleSpinner', null, { root: true });
    let combinedData = [];
    try {
      const response = await measureHttpClient.get('project/get_project_info');
      const measureData = handleResponse(response);
      combinedData.push(...measureData.data);
    } catch (error) {
      console.error('Measure API failed:', error);
    }
    try {
      const response = await carnotHttpClient.get('project/get_project_info');
      const carnotData = handleResponse(response);
      combinedData.push(...carnotData.data);
    } catch (error) {
      console.error('Carnot API failed:', error);
    }
    commit('setProjectsArray', combinedData);
    dispatch('toggleSpinner', null, { root: true });
  },
  async getAllProjectsFlightDates({ commit, state, dispatch }) {
    if (state.projectsFlightDatesList.length || state.isFlightDateFetched) return;

    dispatch('toggleSpinner', null, { root: true });
    try {
      const response = await dataHttpClient.get(`metadata/flight_dates`);
      const flightData = response.data?.data;

      const flightDatesMap = new Map(
        flightData.map(item => [`${item.portal}-${item.project_id}`, item.flight_dates])
      );
      const updateProjectsArray = state.projectsArray.map(item => {
        const flight_dates = flightDatesMap.get(`${item.portal}-${item.id}`) || [];
        return { ...item, flight_dates };
      });
      state.isFlightDateFetched = true;

      commit('setProjectsFlightDatesList', flightData);
      commit('setProjectsArray', updateProjectsArray);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    } finally {
      dispatch('toggleSpinner', null, { root: true });
    }
  },
  async getProjectMetadata({ commit, state, dispatch }, data) {
    const cacheKey = `${data.portal}/${data.id}/${data.flight_date}`;

    const cachedData = state.metadataByProjects?.[cacheKey];
    if (cachedData) {
      commit('setProjectMetadata', cachedData);
      return Promise.resolve(cachedData);
    }

    dispatch('toggleSpinner', null, { root: true });
    try {
      const response = await dataHttpClient.get(`metadata/${cacheKey}`);
      const metaData = response.data?.data;

      for (let i = 0; i < metaData.image_metadata.length; i++) {
        const item = metaData.image_metadata[i];
        const segments = item.path.split('/');
        const rgbIndex = segments.indexOf('RGB');
        if (rgbIndex !== -1 && rgbIndex + 1 < segments.length) {
          item.block = decodeURIComponent(segments[rgbIndex + 1])
            .toUpperCase()
            .replace(/[.\s-]/g, '_');
        } else {
          item.block = '';
        }
        const dateParts = item.datetime.split(' ');
        if (dateParts.length === 2) {
          let [year, month, day] = dateParts[0].split(':');
          let [hour, minute, second] = dateParts[1].split(':');
          hour = parseInt(hour, 10);
          const period = hour >= 12 ? 'PM' : 'AM';
          hour = hour % 12 || 12;
          item.datetime = `${day}/${month}/${year} ${hour}:${minute}:${second} ${period}`;
        }
      }

      commit('cacheMetadataByProjects', { id: cacheKey, data: metaData });
      commit('setProjectMetadata', metaData);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    } finally {
      dispatch('toggleSpinner', null, { root: true });
    }
  },
  async getWeatherData({ commit, state, dispatch }, data) {
    const cacheKey = `${data[0]}_${data[1]}`;
    const cachedWeather = state.weatherByProjects[cacheKey];
    if (cachedWeather) {
      commit('setWeatherData', cachedWeather);
      return Promise.resolve(cachedWeather);
    }

    dispatch('toggleSpinner', null, { root: true });
    try {
      const params = `?lat=${data[0]}&lon=${data[1]}`;
      const response = await measureHttpClient.get(`project/get_weather/${params}`);
      const weatherData = response.data?.data;

      commit('cacheWeatherByProjects', { id: cacheKey, data: weatherData });
      commit('setWeatherData', weatherData);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    } finally {
      dispatch('toggleSpinner', null, { root: true });
    }
  },
  toggleSidebar({ commit }) {
    commit('toggleSidebar');
  },
  selectedProjectData({ commit }, data) {
    commit('setProjectData', data);
  },
  selectedProjectMetadata({ commit }, data) {
    commit('setProjectMetadata', data);
  },
  resetModule({ commit }) {
    commit('resetModule');
  },
};

const mutations = {
  toggleSidebar(state) {
    state.isAddSidebar = !state.isAddSidebar;
  },
  setProjectsArray(state, data) {
    state.projectsArray = data;
  },
  setProjectData(state, data) {
    state.projectSelected = data;
  },
  setProjectsFlightDatesList(state, data) {
    state.projectsFlightDatesList = data;
  },
  setProjectMetadata(state, data) {
    state.projectMetadata = data;
  },
  cacheMetadataByProjects(state, { id, data }) {
    state.metadataByProjects = { ...state.metadataByProjects, [id]: data };
  },
  setWeatherData(state, data) {
    state.weather = data;
  },
  cacheWeatherByProjects(state, { id, data }) {
    state.weatherByProjects = { ...state.weatherByProjects, [id]: data };
  },
  resetModule(state) {
    Object.assign(state, getInitialState());
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
